
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Search, Clock, User } from "lucide-react";
import { Tutorial, BomItem, TutorialSection } from "@/types/tutorial";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface TutorialListProps {
  onViewTutorial: (tutorial: Tutorial) => void;
}

// Type guard to safely cast content from Json to our expected structure
const parseContent = (content: any): { bom: BomItem[]; sections: TutorialSection[] } => {
  if (!content || typeof content !== 'object') {
    return { bom: [], sections: [] };
  }
  
  return {
    bom: Array.isArray(content.bom) ? content.bom : [],
    sections: Array.isArray(content.sections) ? content.sections : []
  };
};

// Type guard for tutorial status
const isValidStatus = (status: string): status is "draft" | "pending" | "approved" | "rejected" => {
  return ['draft', 'pending', 'approved', 'rejected'].includes(status);
};

// Function to create safe HTML content for descriptions
const createSafeHTML = (htmlContent: string) => {
  return { __html: htmlContent };
};

const TutorialList: React.FC<TutorialListProps> = ({ onViewTutorial }) => {
  const { user } = useAuth();
  const [tutorials, setTutorials] = useState<Tutorial[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTutorials();
  }, [user]);

  const loadTutorials = async () => {
    try {
      let query = supabase
        .from('tutorials')
        .select('*')
        .order('created_at', { ascending: false });

      // If user is logged in, show their drafts/pending tutorials plus approved ones
      if (user) {
        query = query.or(`status.eq.approved,created_by.eq.${user.id}`);
      } else {
        // Only show approved tutorials for non-logged-in users
        query = query.eq('status', 'approved');
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error loading tutorials:', error);
        return;
      }

      // Transform the data to match our Tutorial interface
      const transformedTutorials: Tutorial[] = (data || []).map(tutorial => {
        const content = parseContent(tutorial.content);
        const status = isValidStatus(tutorial.status || 'draft')
          ? tutorial.status as "draft" | "pending" | "approved" | "rejected"
          : 'draft';

        return {
          id: tutorial.id,
          title: tutorial.title || 'Untitled Tutorial',
          coverImage: tutorial.cover_image || '',
          shortDescription: tutorial.short_description || '',
          tags: tutorial.tags || [],
          bom: content.bom,
          sections: content.sections,
          status,
          authorId: tutorial.created_by || '',
          createdAt: tutorial.created_at || new Date().toISOString(),
          updatedAt: tutorial.updated_at || tutorial.created_at || new Date().toISOString()
        };
      });

      setTutorials(transformedTutorials);
    } catch (error) {
      console.error('Error loading tutorials:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTutorials = tutorials.filter(tutorial => {
    const matchesSearch = tutorial.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         tutorial.shortDescription.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         tutorial.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return matchesSearch;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved": return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "pending": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
      case "draft": return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
      case "rejected": return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default: return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-muted-foreground">Loading tutorials...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search tutorials..."
            className="pl-10"
          />
        </div>
      </div>

      {/* Tutorial Grid */}
      {filteredTutorials.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTutorials.map((tutorial) => (
            <Card key={tutorial.id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" onClick={() => onViewTutorial(tutorial)}>
              {tutorial.coverImage && (
                <div className="aspect-video bg-muted">
                  <img
                    src={tutorial.coverImage}
                    alt={tutorial.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="p-4 space-y-3">
                <div className="space-y-2">
                  <h3 className="font-semibold text-lg line-clamp-2">{tutorial.title}</h3>
                  <div 
                    className="text-muted-foreground text-sm line-clamp-3 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={createSafeHTML(tutorial.shortDescription)}
                  />
                </div>
                
                <div className="flex items-center gap-2 flex-wrap">
                  {user && tutorial.authorId === user.id && (
                    <Badge className={getStatusColor(tutorial.status)} variant="outline">
                      {tutorial.status}
                    </Badge>
                  )}
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock size={12} />
                    {tutorial.sections.length} sections
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-1">
                  {tutorial.tags.slice(0, 3).map((tag) => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                  {tutorial.tags.length > 3 && (
                    <Badge variant="outline" className="text-xs">
                      +{tutorial.tags.length - 3} more
                    </Badge>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-muted-foreground">
            {searchQuery ? (
              <p>No tutorials found matching your search.</p>
            ) : (
              <div className="space-y-2">
                <p>No tutorials available yet.</p>
                <p className="text-sm">Be the first to create a tutorial!</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TutorialList;
